import { useQuery } from '@apollo/react-hooks'
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonIcon,
  IonLoading,
  IonProgressBar
} from '@ionic/react'
import dayjs from 'dayjs'
import { listOutline } from 'ionicons/icons'
import React, { useState } from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'
import { QUOTE_QUERY, QuoteData, QuoteVariables } from '../graphql/query/quote'
import {
  QUOTE_DOWNLOAD_QUERY,
  QuoteDownloadData,
  QuoteDownloadVariables
} from '../graphql/query/quote.download'
import {
  QUOTE_SIGNATURE_DOWNLOAD_QUERY,
  QuoteSignatureDownloadData,
  QuoteSignatureDownloadVariables
} from '../graphql/query/quote.signature.download'
import { client } from '../lib/client'
import { State } from '../store'
import { updateHistoryEntry, updateHistoryEntryType } from '../store/history'
import { addNotification, addNotificationType } from '../store/notification'
import { addMessage, addMessageType } from '../store/peter'
import { pdfDownloadAction } from './pdf.download'
import { QuoteSignatureModal } from './quote.signature.modal'

interface StateProps {
  accessKey: string
  current: any
  total: number
}

interface DispatchProps {
  addNotification: addNotificationType
  addMessage: addMessageType
  updateHistoryEntry: updateHistoryEntryType
}

type Props = StateProps & DispatchProps & RouteComponentProps<{}>

const CurrentQuoteInternal: React.FC<Props> = (props) => {
  const [showLoading, setShowLoading] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [pollInterval, setPollInterval] = useState(5000)

  const {data} = useQuery<QuoteData, QuoteVariables>(
    QUOTE_QUERY,
    {
      skip: !props.current,
      pollInterval: pollInterval,
      notifyOnNetworkStatusChange: true,
      variables: {
        id: props.current?.id,
        accessKey: props.accessKey,
      },
      onCompleted(response) {
        props.updateHistoryEntry(response.quote)

        if (['done'].includes(response.quote.status)) {
          setPollInterval(0)
        }
      }
    }
  )

  if (!data) {
    return null
  }

  const quote = data.quote

  const downloadQuoteSignature = async () => {
    setShowLoading(true)
    try {
      const response = await client.query<QuoteSignatureDownloadData, QuoteSignatureDownloadVariables>({
        query: QUOTE_SIGNATURE_DOWNLOAD_QUERY,
        variables: {
          id: props.current.id,
          accessKey: props.accessKey,
        }
      })

      await pdfDownloadAction(
        response.data.base64,
        `${quote.gf}.pdf`
      )
    } catch (e) {
      props.addNotification({
        color: 'danger',
        message: 'Konnte Antrag nicht laden'
      })
    }
    setShowLoading(false)
  }

  const downloadQuote = async () => {
    setShowLoading(true)
    props.addMessage({
      message: 'Wunderbar, sehen sie sich das Angebot an!',
      animation: 'convince'
    })
    try {
      const response = await client.query<QuoteDownloadData, QuoteDownloadVariables>({
        query: QUOTE_DOWNLOAD_QUERY,
        variables: {
          id: props.current.id,
          accessKey: props.accessKey,
        }
      })

      await pdfDownloadAction(
        response.data.base64,
        `${quote.gf}.pdf`
      )
    } catch (e) {
      props.addNotification({
        color: 'danger',
        message: 'Konnte Angebot nicht laden'
      })
    }
    setShowLoading(false)
  }

  const content = () => {
    if (quote.status === 'wait_for_signature') {
      const remainingDays = dayjs(quote.created).add(6, 'week').diff(dayjs(), 'day')

      return (
        <div>
          <p>
            Sie haben ihr Angebot bereits erhalten. Sehen sie es an und entscheiden sie selbst ob sie dieses Annehmen wollen.
          </p>


          {remainingDays > 0 ? (
            <div style={{
              marginTop: 8,
              marginBottom: 8,
              fontWeight: 'bold',
            }}>
              Angebot gilt noch für {remainingDays} {remainingDays > 1 ? 'Tage' : 'Tag'}
            </div>
          ) : (
            <div style={{
              marginTop: 8,
              marginBottom: 8,
              fontWeight: 'bold',
            }}>
              Angebot ist bereits abgelaufen
            </div>
          )}

          <QuoteSignatureModal
            gf={quote.gf!}
            id={quote.id}
            visible={showSignature}
            onHide={() => setShowSignature(false)}
          />

          <IonButton color={'tertiary'} onClick={() => downloadQuote()}>
            Angebot Anzeigen
          </IonButton>

          <IonButton disabled={remainingDays < 1} onClick={() => {
            props.addMessage({
              message: 'Bitte füllen sie noch ein paar Felder aus!',
              animation: 'pointLeft'
            })

            setShowSignature(true)
          }}>
            Weiter
          </IonButton>
        </div>
      )
    }

    if (quote.status === 'signed') {
      return (
        <div>
          <p>Wunderbar, wir haben ihre Unterschrift erhalten.</p>

          <h2 style={{
            marginTop: 16,
            marginBottom: 8,
            fontWeight: 'bold',
          }}>Was passiert jetzt?</h2>
          <p style={{
            marginBottom: 8
          }}>
            Die Versicherungsgesellschaft hat die Dokumente erhalten, sie werden benachrichtigt sobald dies abgeschlossen ist.
          </p>

          <IonButton onClick={() => downloadQuoteSignature()}>
            Antrag Anzeigen
          </IonButton>
        </div>
      )
    }

    if (quote.status === 'done') {
      return (
        <div>
          <p>Wunderbar, Polizze {quote.policyNumber} wurde angelegt.</p>

          <h2 style={{
            marginTop: 16,
            marginBottom: 8,
            fontWeight: 'bold',
          }}>Was passiert jetzt?</h2>
          <p style={{
            marginBottom: 8
          }}>
            Geneißen sie den Verischerunsschutz
          </p>

          <IonButton onClick={() => downloadQuoteSignature()}>
            Antrag Anzeigen
          </IonButton>
        </div>
      )
    }

    return (
      <div>
        <IonProgressBar
          type="indeterminate"
          style={{
            height: 20,
            marginBottom: 30,
          }}
        />

        Oha, wir arbeiten noch an ihrem Angebot.
      </div>
    )
  }

  const renderTitle = () => {
    if (quote.policyNumber) {
      return (
        <IonCardTitle>
          Polizze: {quote.policyNumber}
        </IonCardTitle>
      )
    }

    if (quote.gf) {
      return (
        <IonCardTitle>
          GF: {quote.gf}
        </IonCardTitle>
      )
    }

    return (
      <IonCardTitle>
        In Arbeit
      </IonCardTitle>
    )
  }

  return (
    <IonCard style={{'--background': '#FFF'}}>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        message={'PDF wird geladen...'}
      />

      <IonCardHeader>
        {props.total > 0 && (
          <IonButton
            style={{
              position: 'absolute',
              zIndex: 10,
              right: 10,
              top: 10,
            }}
            onClick={() => props.history.push({ pathname: '/list' })}
            fill={'clear'}
          >
            <IonIcon slot="icon-only" icon={listOutline} />
          </IonButton>
        )}
        {renderTitle()}
      </IonCardHeader>

      <IonCardContent>
        {content()}
      </IonCardContent>
    </IonCard>
  )
}

export const CurrentQuote = connect<StateProps, DispatchProps, unknown, State>(
  ({ history }) => {
    const list = Object.values(history.list)
    const current = list.pop()

    return {
      current,
      accessKey: current?.accessKey || 'legacy',
      total: list.filter((e) => e.gf).length,
    }
  },
  {
    addNotification,
    addMessage,
    updateHistoryEntry,
  }
)(withRouter(CurrentQuoteInternal))
